import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { UserFacility } from "@services/facilityManagement";
import {
  facilityService,
  invalidateMyFacilitiesCache,
} from "@services/facilityServices";
import {
  invalidateNetworksCache,
  listNetworks,
  type NetworkRecord,
} from "@services/networkManagement";
import {
  getUserPreferredFacilities,
  type UserPreferredFacility,
} from "@services/payment";
import { mergeSameSiteFacilitiesForDropdown } from "@utils/facilityUtils";
import {
  resolveActiveVertical,
  getPreviewVertical,
  getRequestVerticalOverride,
} from "@services/verticals";
import {
  getSelectedCountryCode,
  userFacilityMatchesSelectedCountryCode,
  userPreferredFacilityMatchesCountryCode,
} from "@utils/countryUtils";
import { getCurrentUserId, userCanUseNetworkFacility } from "@utils/userUtils";
import { markHadFacilityAccess } from "@services/facilityManagement";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  applyMyFacilityRowPatch,
  myFacilityRowToUserFacility,
} from "./facilityLayoutDataUtils";
import {
  mergeSelfReportedPreferredIntoFacilities,
  preferredFacilityToSelectableRow,
  selectableRowFromPostPurchase,
  type SelectablePreferredSource,
} from "@utils/selfReportedPreferredFacility";
import { readPostPurchaseFacilitySelection } from "@utils/postPurchaseFacilitySelection";

export interface RefreshMyFacilitiesOptions {
  bypassCache?: boolean;
  vertical?: string;
}

export interface FacilityLayoutDataContextValue {
  myFacilities: MyFacilityListRow[];
  userFacilities: UserFacility[];
  networks: NetworkRecord[];
  preferredFacilities: UserPreferredFacility[];
  activeVertical: string;
  selectedCountry: string;
  loading: boolean;
  networksLoading: boolean;
  loadError: boolean;
  loaded: boolean;
  refreshMyFacilities: (options?: RefreshMyFacilitiesOptions) => Promise<MyFacilityListRow[]>;
  /** Await layout facilities; reuses in-flight bundle/refresh when possible. */
  ensureMyFacilitiesLoaded: (
    options?: RefreshMyFacilitiesOptions,
  ) => Promise<MyFacilityListRow[]>;
  refreshAll: (options?: { bypassCache?: boolean }) => Promise<void>;
  refreshNetworks: () => Promise<void>;
  /** Merge profile-save (or other) fields into one my-facilities row without refetching. */
  patchMyFacilityRow: (
    facilityId: number,
    patch: Partial<MyFacilityListRow>,
  ) => void;
}

const FacilityLayoutDataContext = createContext<FacilityLayoutDataContextValue | null>(
  null,
);

function bundleKey(country: string, vertical: string, userId: string): string {
  return `${userId}\0${country}\0${vertical}`;
}

function readPreferredSessionBlob(): SelectablePreferredSource | null {
  try {
    const raw = sessionStorage.getItem("preferred_facility_for_pricing");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SelectablePreferredSource;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function withSelfReportedFacilities(
  rows: MyFacilityListRow[],
  preferred: UserPreferredFacility[],
): MyFacilityListRow[] {
  try {
    const preferredSession = readPreferredSessionBlob();
    return mergeSelfReportedPreferredIntoFacilities(
      rows,
      preferred,
      [
        preferredFacilityToSelectableRow(preferredSession || {}),
        selectableRowFromPostPurchase(
          readPostPurchaseFacilitySelection(),
          preferredSession,
        ),
      ],
    );
  } catch {
    return rows;
  }
}

const MY_FACILITIES_EMPTY_RETRY_MS = 2_000;

export function FacilityLayoutDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [myFacilities, setMyFacilities] = useState<MyFacilityListRow[]>([]);
  const [networks, setNetworks] = useState<NetworkRecord[]>([]);
  const [preferredFacilities, setPreferredFacilities] = useState<UserPreferredFacility[]>(
    [],
  );
  const [activeVertical, setActiveVertical] = useState("healthcare");
  const [selectedCountry, setSelectedCountry] = useState(getSelectedCountryCode());
  const [loading, setLoading] = useState(false);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const bundleInFlightRef = useRef<Promise<MyFacilityListRow[]> | null>(null);
  const bundleKeyRef = useRef<string | null>(null);

  const resolveVertical = useCallback(async (): Promise<string> => {
    try {
      const override = getRequestVerticalOverride();
      if (override) return override;
      const preview = getPreviewVertical();
      if (preview) return preview;
      return (await resolveActiveVertical()) || "healthcare";
    } catch {
      return "healthcare";
    }
  }, []);

  const applyPreferred = useCallback(
    (preferred: UserPreferredFacility[]) => {
      const wantCc = String(getSelectedCountryCode() || "")
        .trim()
        .toUpperCase();
      const filtered = preferred
        .filter((f) => !f.is_delete)
        .filter((f) => userPreferredFacilityMatchesCountryCode(f, wantCc));
      setPreferredFacilities(filtered);
    },
    [],
  );

  const loadBundle = useCallback(
    async (options?: {
      bypassCache?: boolean;
      vertical?: string;
    }): Promise<MyFacilityListRow[]> => {
      const userId = getCurrentUserId();
      const country = getSelectedCountryCode();
      const resolvedVertical =
        options?.vertical || (await resolveVertical()) || "healthcare";
      const vertical = String(resolvedVertical).trim().toLowerCase() || "healthcare";
      const key = bundleKey(country, vertical, userId);

      if (bundleInFlightRef.current && bundleKeyRef.current === key) {
        return bundleInFlightRef.current;
      }

      const run = async (): Promise<MyFacilityListRow[]> => {
        setLoading(true);
        setNetworksLoading(true);
        setLoadError(false);
        setActiveVertical(vertical);
        setSelectedCountry(country);

        if (userId === "0") {
          setMyFacilities([]);
          setNetworks([]);
          setPreferredFacilities([]);
          setLoaded(true);
          setLoading(false);
          setNetworksLoading(false);
          return [];
        }

        const numericUserId = parseInt(userId, 10);
        const wantCc = String(country || "").trim().toUpperCase();

        try {
          if (options?.bypassCache) {
            invalidateMyFacilitiesCache();
          }

          const fetchMyFacilities = async (bypassCache: boolean) => {
            const rawMy = await facilityService.getMyFacilities({
              vertical,
              ...(bypassCache ? { bypassCache: true } : {}),
            });
            const seenIds = new Set<number>();
            return mergeSameSiteFacilitiesForDropdown(
              rawMy.filter((f) => {
                const id = f.id ?? f.facility_id;
                if (seenIds.has(id)) return false;
                seenIds.add(id);
                return userFacilityMatchesSelectedCountryCode(
                  myFacilityRowToUserFacility(f),
                  wantCc,
                );
              }),
            );
          };

          const [dedupedMyInitial, networkResult, preferredResult] = await Promise.all([
            fetchMyFacilities(Boolean(options?.bypassCache)),
            userCanUseNetworkFacility()
              ? listNetworks()
                  .then((rows) => ({ rows, failed: false }))
                  .catch(() => ({ rows: [] as NetworkRecord[], failed: true }))
              : Promise.resolve({
                  rows: [] as NetworkRecord[],
                  failed: false,
                }),
            getUserPreferredFacilities(1, 50, numericUserId)
              .then((response) => ({ response, failed: false }))
              .catch(() => ({
                response: null,
                failed: true,
              })),
          ]);
          const networkRows = networkResult.rows;

          let dedupedMy = dedupedMyInitial;
          if (
            dedupedMy.length === 0 &&
            userId !== "0" &&
            !options?.bypassCache
          ) {
            await new Promise((resolve) =>
              setTimeout(resolve, MY_FACILITIES_EMPTY_RETRY_MS),
            );
            invalidateMyFacilitiesCache();
            dedupedMy = await fetchMyFacilities(true);
          }

          setMyFacilities(dedupedMy);
          setNetworks(networkRows);
          const preferredRows = preferredResult.response?.result?.data ?? [];
          applyPreferred(preferredRows);
          // Optional network/preference failures must not discard valid facilities.
          // Existing UI uses this flag for network failure messaging.
          setLoadError(networkResult.failed);
          const merged = withSelfReportedFacilities(dedupedMy, preferredRows);
          if (merged.length > 0) {
            markHadFacilityAccess();
          }
          return merged;
        } catch {
          setLoadError(true);
          return [];
        } finally {
          setLoading(false);
          setNetworksLoading(false);
          setLoaded(true);
        }
      };

      bundleKeyRef.current = key;
      const promise = run().finally(() => {
        if (bundleInFlightRef.current === promise) {
          bundleInFlightRef.current = null;
        }
      });
      bundleInFlightRef.current = promise;
      return promise;
    },
    [applyPreferred, resolveVertical],
  );

  const myFacilitiesRefreshInFlightRef = useRef<Promise<MyFacilityListRow[]> | null>(
    null,
  );

  const dedupeMyFacilitiesRows = useCallback((rows: MyFacilityListRow[]) => {
    const wantCc = getSelectedCountryCode().trim().toUpperCase();
    const seenIds = new Set<number>();
    return mergeSameSiteFacilitiesForDropdown(
      rows.filter((f) => {
        const id = f.id ?? f.facility_id;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return userFacilityMatchesSelectedCountryCode(
          myFacilityRowToUserFacility(f),
          wantCc,
        );
      }),
    );
  }, []);

  const refreshMyFacilities = useCallback(
    async (options?: RefreshMyFacilitiesOptions): Promise<MyFacilityListRow[]> => {
      const vertical =
        (options?.vertical || activeVertical).trim().toLowerCase() || "healthcare";

      const run = async (): Promise<MyFacilityListRow[]> => {
        try {
          if (options?.bypassCache) {
            invalidateMyFacilitiesCache();
          }
          const rows = await facilityService.getMyFacilities({
            vertical,
            ...(options?.bypassCache ? { bypassCache: true } : {}),
          });
          const deduped = dedupeMyFacilitiesRows(rows);
          setMyFacilities(deduped);
          setActiveVertical(vertical);
          const merged = withSelfReportedFacilities(deduped, preferredFacilities);
          if (merged.length > 0) {
            markHadFacilityAccess();
          }
          setLoadError(false);
          return merged;
        } catch {
          setLoadError(true);
          return [];
        }
      };

      if (myFacilitiesRefreshInFlightRef.current) {
        return myFacilitiesRefreshInFlightRef.current;
      }
      const promise = run().finally(() => {
        if (myFacilitiesRefreshInFlightRef.current === promise) {
          myFacilitiesRefreshInFlightRef.current = null;
        }
      });
      myFacilitiesRefreshInFlightRef.current = promise;
      return promise;
    },
    [activeVertical, dedupeMyFacilitiesRows, preferredFacilities],
  );

  const ensureMyFacilitiesLoaded = useCallback(
    async (options?: RefreshMyFacilitiesOptions): Promise<MyFacilityListRow[]> => {
      const vertical =
        (options?.vertical || activeVertical).trim().toLowerCase() || "healthcare";
      const country = getSelectedCountryCode().trim().toUpperCase();

      if (
        !options?.bypassCache &&
        loaded &&
        activeVertical === vertical &&
        selectedCountry === country &&
        myFacilities.length > 0
      ) {
        return withSelfReportedFacilities(myFacilities, preferredFacilities);
      }

      if (options?.bypassCache) {
        return refreshMyFacilities({ bypassCache: true, vertical });
      }

      if (!loaded || activeVertical !== vertical || selectedCountry !== country) {
        return loadBundle({ vertical });
      }

      if (myFacilities.length > 0) {
        return withSelfReportedFacilities(myFacilities, preferredFacilities);
      }

      return refreshMyFacilities({ vertical, bypassCache: true });
    },
    [
      activeVertical,
      loadBundle,
      loaded,
      myFacilities,
      preferredFacilities,
      refreshMyFacilities,
      selectedCountry,
    ],
  );

  const refreshNetworks = useCallback(async () => {
    const userId = getCurrentUserId();
    if (userId === "0" || !userCanUseNetworkFacility()) {
      setNetworks([]);
      return;
    }
    setNetworksLoading(true);
    invalidateNetworksCache();
    try {
      const networkRows = await listNetworks({ bypassCache: true });
      setNetworks(networkRows);
    } catch {
      setNetworks([]);
    } finally {
      setNetworksLoading(false);
    }
  }, []);

  const refreshAll = useCallback(
    async (options?: { bypassCache?: boolean }) => {
      await loadBundle(options);
    },
    [loadBundle],
  );

  const patchMyFacilityRow = useCallback(
    (facilityId: number, patch: Partial<MyFacilityListRow>) => {
      setMyFacilities((prev) => applyMyFacilityRowPatch(prev, facilityId, patch));
    },
    [],
  );

  useEffect(() => {
    void loadBundle();
  }, [loadBundle]);

  useEffect(() => {
    const onCountry = () => {
      const current = getSelectedCountryCode();
      if (current !== selectedCountry) {
        setSelectedCountry(current);
        void loadBundle();
      }
    };
    const interval = setInterval(onCountry, 500);
    return () => clearInterval(interval);
  }, [loadBundle, selectedCountry]);

  useEffect(() => {
    const onVertical = () => {
      void loadBundle();
    };
    window.addEventListener(
      "unicomply:preview-vertical-changed",
      onVertical as EventListener,
    );
    return () => {
      window.removeEventListener(
        "unicomply:preview-vertical-changed",
        onVertical as EventListener,
      );
    };
  }, [loadBundle]);

  useEffect(() => {
    const onNetworks = () => {
      void refreshNetworks();
    };
    window.addEventListener("unicomply:networks-changed", onNetworks as EventListener);
    return () => {
      window.removeEventListener("unicomply:networks-changed", onNetworks as EventListener);
    };
  }, [refreshNetworks]);

  const mergedMyFacilities = useMemo(
    () => withSelfReportedFacilities(myFacilities, preferredFacilities),
    [myFacilities, preferredFacilities],
  );

  const userFacilities = useMemo(
    () => mergedMyFacilities.map(myFacilityRowToUserFacility),
    [mergedMyFacilities],
  );

  const value = useMemo<FacilityLayoutDataContextValue>(
    () => ({
      myFacilities: mergedMyFacilities,
      userFacilities,
      networks,
      preferredFacilities,
      activeVertical,
      selectedCountry,
      loading,
      networksLoading,
      loadError,
      loaded,
      refreshMyFacilities,
      ensureMyFacilitiesLoaded,
      refreshAll,
      refreshNetworks,
      patchMyFacilityRow,
    }),
    [
      mergedMyFacilities,
      userFacilities,
      networks,
      preferredFacilities,
      activeVertical,
      selectedCountry,
      loading,
      networksLoading,
      loadError,
      loaded,
      refreshMyFacilities,
      ensureMyFacilitiesLoaded,
      refreshAll,
      refreshNetworks,
      patchMyFacilityRow,
    ],
  );

  return (
    <FacilityLayoutDataContext.Provider value={value}>
      {children}
    </FacilityLayoutDataContext.Provider>
  );
}

export function useFacilityLayoutData(): FacilityLayoutDataContextValue {
  const ctx = useContext(FacilityLayoutDataContext);
  if (!ctx) {
    throw new Error("useFacilityLayoutData must be used within FacilityLayoutDataProvider");
  }
  return ctx;
}

/** Optional hook for components that may render outside the dashboard shell. */
export function useFacilityLayoutDataOptional(): FacilityLayoutDataContextValue | null {
  return useContext(FacilityLayoutDataContext);
}
